/* src/views/logements/proto/InfosProto.jsx — LES PIÈCES DE L'ONGLET « Infos »
   de la fiche logement, transcrites de docs/handoff/logements-1-fiche.jsx
   (FicheInfos, livré 2026-08-27) : la jauge « Ce qui est réglé », les blocs
   accordéon (fermé = carte blanche + chevron ; ouvert = liseré ciel + en-tête
   #dbeaf7), les lignes libellé/valeur, les renvois « Ce qui se règle
   ailleurs ». LE DESSIN, PAS LA LOGIQUE — styles copiés tels quels, valeurs
   en props, gestes en onXxx.

   Le prototype ne dessine l'intérieur QUE du bloc Identité : les autres blocs
   se composent dans le MÊME idiome (lignes, notes) — c'est la consigne du
   6e bloc (« même dessin que Prix et Caution, dépliable comme les autres »). */

import React from 'react'

/* ── la jauge « Ce qui est réglé » (cercle N/total) ───────────────── */
export function JaugeReglage({ n, total }) {
  const CIRC = 157.07963267948966 // 2π×25, valeur du proto
  const offset = total ? CIRC * (1 - n / total) : CIRC
  const manquants = Math.max(0, total - n)
  return (
    <div style={{ background: '#fff', borderRadius: '16px', padding: '14px',
      marginBottom: '12px',
      boxShadow: '0 1px 3px rgba(35,50,64,.08),0 0 0 1px rgba(35,50,64,.05)' }}
      data-testid="infos-jauge">
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        <div style={{ flex: '0 0 auto' }}>
          <span style={{ position: 'relative', display: 'inline-flex', width: '60px',
            height: '60px', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="60" height="60" viewBox="0 0 60 60"
              style={{ position: 'absolute', transform: 'rotate(-90deg)' }} aria-hidden="true">
              <circle cx="30" cy="30" r="25" fill="none" stroke="#eef2f6" strokeWidth="4.5" />
              <circle cx="30" cy="30" r="25" fill="none" stroke="#87b8e6" strokeWidth="4.5"
                strokeLinecap="round" strokeDasharray={CIRC} strokeDashoffset={offset} />
            </svg>
            <span style={{ position: 'relative', fontSize: '16px', fontWeight: '600',
              color: 'var(--navy-d)', lineHeight: '1' }}>{n}<span style={{ fontSize: '10px',
              color: 'var(--gris-d)', fontWeight: '400' }}>/{total}</span>
            </span>
          </span>
        </div>
        <div style={{ flex: '1', minWidth: '0' }}>
          <p style={{ fontSize: '10px', color: 'var(--gris)', letterSpacing: '.06em',
            textTransform: 'uppercase', fontWeight: '600', margin: '0' }}>Ce qui est réglé</p>
          <p style={{ fontSize: '14.5px', fontWeight: '600', color: 'var(--navy-d)',
            lineHeight: '1.3', margin: '4px 0 0' }}>
            {manquants === 0 ? 'tout est réglé'
              : `${manquants} bloc${manquants > 1 ? 's' : ''} à compléter`}</p>
          <p style={{ fontSize: '11px', color: 'var(--gris)', margin: '3px 0 0',
            lineHeight: '1.45' }}>les blocs qui manquent portent un point orange</p>
        </div>
      </div>
    </div>
  )
}

/* ── un bloc accordéon (fermé → carte ; ouvert → liseré ciel) ─────── */
export function BlocInfo({ id, ouvert, onToggle, icone, titre, sousTitre,
  aCompleter = false, verrou = false, enfants }) {
  const iconeStyle = ouvert
    ? { background: '#87b8e6', color: '#fff' }
    : (aCompleter
      ? { background: 'var(--ambre-bg)', color: 'var(--ambre-tx)' }
      : { background: 'var(--vert-bg)', color: 'var(--vert-tx)' })
  return (
    <div style={{ background: '#fff', borderRadius: '14px', marginBottom: '8px',
      overflow: 'hidden',
      boxShadow: ouvert
        ? '0 1px 3px rgba(35,50,64,.08),0 0 0 1px #87b8e6'
        : '0 1px 3px rgba(35,50,64,.08),0 0 0 1px rgba(35,50,64,.05)' }}>
      <button onClick={onToggle} data-testid={`infos-bloc-${id}`}
        aria-expanded={ouvert}
        style={{ display: 'flex', alignItems: 'center', gap: '11px', width: '100%',
          textAlign: 'left', background: ouvert ? '#dbeaf7' : '#fff', border: 'none',
          padding: '11px 12px', minHeight: '60px', cursor: 'pointer' }}>
        <span style={{ width: '32px', height: '32px', borderRadius: '9px',
          flexShrink: '0', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: '14px', ...iconeStyle }}>{icone}</span>
        <span style={{ flex: '1', minWidth: '0' }}>
          <span style={{ display: 'block', fontSize: '12.5px', fontWeight: '600',
            color: 'var(--navy-d)' }}>{titre}</span>
          <span style={{ display: 'block', fontSize: '10.5px',
            color: aCompleter ? 'var(--ambre-tx)' : 'var(--gris)', marginTop: '1px',
            overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap' }}>{sousTitre}</span>
        </span>
        {aCompleter && (
          <span style={{ width: '7px', height: '7px', borderRadius: '50%',
            background: '#d98c1f', flexShrink: '0' }} />
        )}
        {verrou && (
          <span style={{ fontSize: '10px', color: 'var(--gris-d)', flexShrink: '0' }}>🔒</span>
        )}
        <span style={{ fontSize: '15px', color: 'var(--gris-d)', flexShrink: '0',
          transform: ouvert ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform .15s' }}>›</span>
      </button>
      {ouvert && <div style={{ padding: '12px' }}>{enfants}</div>}
    </div>
  )
}

/* ── une ligne libellé / valeur (filet bas) ───────────────────────── */
export function LigneKV({ label, dernier = false, children }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between',
      alignItems: 'center', gap: '12px', padding: '8px 0',
      borderBottom: dernier ? 'none' : '1px solid var(--bord)' }}>
      <span style={{ fontSize: '12px', color: 'var(--gris)', flexShrink: 0 }}>{label}</span>
      {children}
    </div>
  )
}

/* ── une ligne pleine largeur (libellé au-dessus) ─────────────────── */
export function LignePleine({ label, dernier = false, children }) {
  return (
    <div style={{ padding: '8px 0',
      borderBottom: dernier ? 'none' : '1px solid var(--bord)' }}>
      <span style={{ display: 'block', fontSize: '12px', color: 'var(--gris)',
        marginBottom: '7px' }}>{label}</span>
      {children}
    </div>
  )
}

/* ── un champ de saisie dans l'idiome du proto ────────────────────── */
export function ChampProto({ value, onChange, placeholder, type = 'text',
  testId, disabled = false, large = false, suffixe }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px',
      width: large ? '100%' : undefined }}>
      <input type={type} value={value} disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.value)}
        placeholder={placeholder} data-testid={testId}
        style={{ minHeight: '38px', padding: '0 12px', borderRadius: '9px',
          background: disabled ? '#eef2f6' : '#f8fafc', border: '1px solid #d5e3ee',
          fontSize: '12.5px', fontWeight: '600', color: 'var(--navy-d)',
          width: large ? '100%' : '150px', outline: 'none',
          fontFamily: 'inherit' }} />
      {suffixe && <span style={{ fontSize: '12px', color: 'var(--gris)' }}>{suffixe}</span>}
    </span>
  )
}

/* ── un groupe de boutons segmentés (statut, mode de commission…) ─── */
export function Segmentes({ options, valeur, onChoisir, testIdPrefix, disabled = false }) {
  return (
    <span style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {options.map((o) => {
        const actif = valeur === o.value
        return (
          <button key={o.value} onClick={() => !disabled && onChoisir(o.value)}
            disabled={disabled}
            data-testid={testIdPrefix ? `${testIdPrefix}-${o.value}` : undefined}
            style={actif
              ? { minHeight: '36px', padding: '0 12px', borderRadius: '9px',
                fontSize: '11.5px', background: 'var(--vert-bg)',
                border: '1px solid var(--vert-bd)', color: 'var(--vert-tx)',
                fontWeight: '600', cursor: disabled ? 'not-allowed' : 'pointer' }
              : { minHeight: '36px', padding: '0 12px', borderRadius: '9px',
                fontSize: '11.5px', background: disabled ? '#eef2f6' : '#fff',
                border: '1px solid #d5e3ee',
                color: 'var(--gris)', cursor: disabled ? 'not-allowed' : 'pointer' }}>{o.label}</button>
        )
      })}
    </span>
  )
}

/* ── un renvoi « Ce qui se règle ailleurs » ───────────────────────── */
export function RenvoiReglage({ titre, sousTitre, onClick, testId, point = false }) {
  return (
    <button onClick={onClick} data-testid={testId}
      style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
        textAlign: 'left', background: '#f8fafc', border: '1px solid var(--bord)',
        borderRadius: '12px', padding: '10px 12px', minHeight: '54px',
        marginBottom: '7px', cursor: 'pointer' }}>
      <span style={{ width: '26px', height: '26px', borderRadius: '8px',
        background: '#eef2f6', color: '#5c6773', display: 'flex',
        alignItems: 'center', justifyContent: 'center', fontSize: '12px',
        flexShrink: '0' }}>⚙</span>
      <span style={{ flex: '1', minWidth: '0' }}>
        <span style={{ display: 'block', fontSize: '11.5px', fontWeight: '600',
          color: 'var(--navy-d)' }}>{titre}</span>
        <span style={{ display: 'block', fontSize: '10px', color: 'var(--gris)',
          marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap' }}>{sousTitre}</span>
      </span>
      {point && (
        <span style={{ width: '7px', height: '7px', borderRadius: '50%',
          background: '#d98c1f', flexShrink: '0' }} />
      )}
      <span style={{ fontSize: '14px', color: 'var(--gris-d)',
        flexShrink: '0' }}>›</span>
    </button>
  )
}
